import type {
	UploadProgressMessageV2,
	UploadProgressSource,
	UploadProgressStage,
} from '@sosocio/creator-types/app';
import { normalizeUploadProgressPercentage } from './external-upload-helpers';

export const EXTERNAL_UPLOAD_RECOVERY_TTL_MS = 15 * 60 * 1000;

const EXTERNAL_UPLOAD_RECOVERY_KEY = 'creator.external-upload.recovery.v1';

export type ExternalUploadRecoveryScope = {
	productId: string;
	userId: string;
};

export type ExternalUploadRecoveryMetadata = ExternalUploadRecoveryScope & {
	batchId: string;
	expiresAt: number;
	processedCount: number;
	progressPercentage: number;
	sequence: number;
	source: UploadProgressSource;
	stage: UploadProgressStage;
	submittedCount: number;
	transferredCount: number;
	uploadSessionId: string;
	version: 1;
};

type RecoveryStorage = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

const recoverableStages: ReadonlySet<UploadProgressStage> = new Set([
	'processing',
	'saving',
	'selected',
	'transferring',
]);

const recoverySources: ReadonlySet<UploadProgressSource> = new Set([
	'external-device',
	'native-picker',
]);

/**
 * Validates counters stored at the session-storage trust boundary.
 *
 * @param value Unknown persisted value.
 * @returns Whether the value is a non-negative integer.
 */
function isNonNegativeInteger(value: unknown): value is number {
	return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Validates the complete persisted recovery record before it is consumed.
 *
 * @param value Parsed session-storage value.
 * @returns Whether the value satisfies {@link ExternalUploadRecoveryMetadata}.
 */
function isRecoveryMetadata(value: unknown): value is ExternalUploadRecoveryMetadata {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return false;
	}

	const metadata = value as Record<string, unknown>;

	return (
		metadata.version === 1
		&& typeof metadata.productId === 'string'
		&& metadata.productId.length > 0
		&& typeof metadata.userId === 'string'
		&& metadata.userId.length > 0
		&& typeof metadata.batchId === 'string'
		&& metadata.batchId.length > 0
		&& typeof metadata.uploadSessionId === 'string'
		&& metadata.uploadSessionId.length > 0
		&& typeof metadata.expiresAt === 'number'
		&& Number.isFinite(metadata.expiresAt)
		&& typeof metadata.progressPercentage === 'number'
		&& Number.isFinite(metadata.progressPercentage)
		&& metadata.progressPercentage >= 0
		&& metadata.progressPercentage <= 100
		&& isNonNegativeInteger(metadata.sequence)
		&& isNonNegativeInteger(metadata.processedCount)
		&& isNonNegativeInteger(metadata.submittedCount)
		&& isNonNegativeInteger(metadata.transferredCount)
		&& metadata.processedCount <= metadata.submittedCount
		&& metadata.processedCount <= metadata.transferredCount
		&& metadata.transferredCount <= metadata.submittedCount
		&& typeof metadata.source === 'string'
		&& recoverySources.has(metadata.source as UploadProgressSource)
		&& typeof metadata.stage === 'string'
		&& recoverableStages.has(metadata.stage as UploadProgressStage)
	);
}

/**
 * Persists only correlation, counters, and lifecycle state for a non-terminal
 * upload. Files, signed photo URLs, and device/account details are deliberately
 * excluded from the recovery record.
 *
 * @param storage Session-scoped storage implementation.
 * @param scope Active product/user scope.
 * @param message Validated protocol-v2 progress message.
 * @param now Current epoch timestamp used to calculate expiry.
 */
export function persistExternalUploadRecovery(
	storage: RecoveryStorage,
	scope: ExternalUploadRecoveryScope,
	message: UploadProgressMessageV2,
	now = Date.now(),
): void {
	if (!recoverableStages.has(message.stage)) {
		clearExternalUploadRecovery(storage);
		return;
	}

	const metadata: ExternalUploadRecoveryMetadata = {
		batchId: message.batchId,
		expiresAt: now + EXTERNAL_UPLOAD_RECOVERY_TTL_MS,
		processedCount: message.processedCount,
		productId: scope.productId,
		progressPercentage: normalizeUploadProgressPercentage(message),
		sequence: message.sequence,
		source: message.source,
		stage: message.stage,
		submittedCount: message.submittedCount,
		transferredCount: message.transferredCount,
		uploadSessionId: message.uploadSessionId,
		userId: scope.userId,
		version: 1,
	};

	try {
		storage.setItem(
			EXTERNAL_UPLOAD_RECOVERY_KEY,
			JSON.stringify(metadata),
		);
	} catch {
		/** Session storage is best-effort and may be unavailable or full. */
	}
}

/**
 * Loads a matching, unexpired recovery record and removes malformed/expired
 * data. A record for another user/product is ignored without deleting it.
 *
 * @param storage Session-scoped storage implementation.
 * @param scope Active product/user scope.
 * @param now Current epoch timestamp used to evaluate expiry.
 * @returns Valid recovery metadata for the active scope, or `null`.
 */
export function loadExternalUploadRecovery(
	storage: RecoveryStorage,
	scope: ExternalUploadRecoveryScope,
	now = Date.now(),
): ExternalUploadRecoveryMetadata | null {
	let value: unknown;

	try {
		const serialized = storage.getItem(EXTERNAL_UPLOAD_RECOVERY_KEY);

		if (!serialized) {
			return null;
		}

		value = JSON.parse(serialized);
	} catch {
		clearExternalUploadRecovery(storage);
		return null;
	}

	if (!isRecoveryMetadata(value) || value.expiresAt <= now) {
		clearExternalUploadRecovery(storage);
		return null;
	}

	if (value.productId !== scope.productId || value.userId !== scope.userId) {
		return null;
	}

	return value;
}

/**
 * Removes the active session's recovery marker without surfacing storage errors.
 *
 * @param storage Session-scoped storage implementation.
 */
export function clearExternalUploadRecovery(storage: RecoveryStorage): void {
	try {
		storage.removeItem(EXTERNAL_UPLOAD_RECOVERY_KEY);
	} catch {
		/** Session storage is best-effort and may be unavailable. */
	}
}

/**
 * Converts minimal recovery metadata back to a URL-free protocol message.
 *
 * @param metadata Validated, unexpired recovery record.
 * @returns Protocol-v2 message suitable for presentation/reconciliation.
 */
export function recoveryMetadataToProgressMessage(
	metadata: ExternalUploadRecoveryMetadata,
): UploadProgressMessageV2 {
	return {
		batchId: metadata.batchId,
		processedCount: metadata.processedCount,
		progressPercentage: metadata.progressPercentage,
		sentAt: new Date().toISOString(),
		sequence: metadata.sequence,
		source: metadata.source,
		stage: metadata.stage,
		submittedCount: metadata.submittedCount,
		transferredCount: metadata.transferredCount,
		uploadSessionId: metadata.uploadSessionId,
		version: 2,
	};
}
